import React, { useState } from 'react';
import { 
  User, 
  Calendar, 
  Clock, 
  Star, 
  MessageSquare, 
  CheckCircle, 
  XCircle, 
  AlertCircle,
  Send,
  FileText,
  Users,
  Target,
  Award
} from 'lucide-react';

const InterviewFeedback = () => {
  const fontStyle = {
    fontFamily: "'Roboto', 'Poppins', sans-serif"
  };
  const [selectedCandidate, setSelectedCandidate] = useState(null);
  const [feedbackData, setFeedbackData] = useState({
    overallRating: 0,
    technicalSkills: 0,
    communication: 0,
    problemSolving: 0,
    culturalFit: 0,
    recommendation: '',
    strengths: '',
    weaknesses: '',
    comments: '',
    nextRoundRecommendation: ''
  });

  // Mock data for pending interviews
  const pendingInterviews = [
    {
      id: 1,
      candidateName: "John Smith",
      position: "Senior React Developer",
      round: "Screening",
      date: "2024-01-15",
      time: "10:00 AM",
      interviewer: "Sarah Johnson",
      status: "pending"
    },
    {
      id: 2,
      candidateName: "Emily Davis",
      position: "UI/UX Designer",
      round: "First Round",
      date: "2024-01-15",
      time: "2:00 PM",
      interviewer: "Mike Wilson",
      status: "pending"
    },
    {
      id: 3,
      candidateName: "Alex Chen",
      position: "Full Stack Developer",
      round: "Client Round",
      date: "2024-01-16",
      time: "11:00 AM",
      interviewer: "David Brown",
      status: "pending"
    },
    {
      id: 4,
      candidateName: "Lisa Rodriguez",
      position: "Product Manager",
      round: "HR Round",
      date: "2024-01-16",
      time: "3:30 PM",
      interviewer: "Jennifer Lee",
      status: "pending"
    }
  ];

  const handleRatingChange = (field, rating) => {
    setFeedbackData(prev => ({
      ...prev,
      [field]: rating
    }));
  };

  const handleInputChange = (field, value) => {
    setFeedbackData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSubmitFeedback = () => {
    if (!selectedCandidate) return;
    
    // Here you would typically send the feedback to your backend
    console.log('Submitting feedback:', {
      candidateId: selectedCandidate.id,
      ...feedbackData
    });
    
    alert('Feedback submitted successfully!');
    setSelectedCandidate(null);
    setFeedbackData({
      overallRating: 0,
      technicalSkills: 0,
      communication: 0,
      problemSolving: 0,
      culturalFit: 0,
      recommendation: '',
      strengths: '',
      weaknesses: '',
      comments: '',
      nextRoundRecommendation: ''
    });
  };

  const StarRating = ({ rating, onRatingChange, label }) => (
    <div className="flex flex-col gap-2">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <Star
            key={star}
            size={24}
            className={`cursor-pointer transition-colors ${
              star <= rating 
                ? 'text-yellow-400 fill-yellow-400' 
                : 'text-gray-300 hover:text-yellow-200'
            }`}
            onClick={() => onRatingChange(star)}
          />
        ))}
      </div>
    </div>
  );

  const getRoundIcon = (round) => {
    switch (round) {
      case 'Screening': return <Users className="text-blue-600" size={20} />;
      case 'First Round': return <Target className="text-green-600" size={20} />;
      case 'Client Round': return <Award className="text-purple-600" size={20} />;
      case 'HR Round': return <User className="text-orange-600" size={20} />;
      default: return <FileText className="text-gray-600" size={20} />;
    }
  };

  const getRoundColor = (round) => {
    switch (round) {
      case 'Screening': return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'First Round': return 'bg-green-100 text-green-800 border-green-200';
      case 'Client Round': return 'bg-purple-100 text-purple-800 border-purple-200';
      case 'HR Round': return 'bg-orange-100 text-orange-800 border-orange-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (selectedCandidate) {
    return (
      <div className="min-h-screen bg-gray-50 p-6" style={fontStyle}>
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setSelectedCandidate(null)}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  ←
                </button>
                <div>
                  <h1 className="text-2xl font-bold text-gray-900">Interview Feedback</h1>
                  <p className="text-gray-600">Submit feedback for {selectedCandidate.candidateName}</p>
                </div>
              </div>
              <div className={`px-4 py-2 rounded-full border text-sm font-medium flex items-center gap-2 ${getRoundColor(selectedCandidate.round)}`}>
                {getRoundIcon(selectedCandidate.round)}
                {selectedCandidate.round}
              </div>
            </div>
          </div>

          {/* Candidate Info */}
          <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
            <h2 className="text-lg font-semibold mb-4 flex items-center gap-2">
              <User size={20} className="text-blue-600" />
              Candidate Information
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-sm text-gray-500">Name</label>
                <p className="font-medium">{selectedCandidate.candidateName}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Position</label>
                <p className="font-medium">{selectedCandidate.position}</p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Date</label>
                <p className="font-medium flex items-center gap-1">
                  <Calendar size={16} />
                  {selectedCandidate.date}
                </p>
              </div>
              <div>
                <label className="text-sm text-gray-500">Time</label>
                <p className="font-medium flex items-center gap-1">
                  <Clock size={16} />
                  {selectedCandidate.time}
                </p>
              </div>
            </div>
          </div>

          {/* Feedback Form */}
          <div className="bg-white rounded-xl shadow-sm border p-6">
            <h2 className="text-lg font-semibold mb-6 flex items-center gap-2">
              <MessageSquare size={20} className="text-green-600" />
              Interview Feedback
            </h2>

            <div className="space-y-8">
              {/* Rating Section */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <StarRating
                  rating={feedbackData.overallRating}
                  onRatingChange={(rating) => handleRatingChange('overallRating', rating)}
                  label="Overall Rating"
                />
                <StarRating
                  rating={feedbackData.technicalSkills}
                  onRatingChange={(rating) => handleRatingChange('technicalSkills', rating)}
                  label="Technical Skills"
                />
                <StarRating
                  rating={feedbackData.communication}
                  onRatingChange={(rating) => handleRatingChange('communication', rating)}
                  label="Communication"
                />
                <StarRating
                  rating={feedbackData.problemSolving}
                  onRatingChange={(rating) => handleRatingChange('problemSolving', rating)}
                  label="Problem Solving"
                />
                <StarRating
                  rating={feedbackData.culturalFit}
                  onRatingChange={(rating) => handleRatingChange('culturalFit', rating)}
                  label="Cultural Fit"
                />
              </div>

              {/* Recommendation */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Recommendation
                </label>
                <div className="flex gap-4">
                  {[
                    { value: 'strongly_recommend', label: 'Strongly Recommend', icon: CheckCircle, color: 'text-green-600' },
                    { value: 'recommend', label: 'Recommend', icon: CheckCircle, color: 'text-blue-600' },
                    { value: 'Hold', label: 'Hold', icon: AlertCircle, color: 'text-yellow-600' },
                    { value: 'not_recommend', label: 'Not Recommend', icon: XCircle, color: 'text-red-600' }
                  ].map((option) => (
                    <label key={option.value} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="recommendation"
                        value={option.value}
                        checked={feedbackData.recommendation === option.value}
                        onChange={(e) => handleInputChange('recommendation', e.target.value)}
                        className="sr-only"
                      />
                      <div className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-colors ${
                        feedbackData.recommendation === option.value
                          ? 'bg-blue-50 border-blue-300 text-blue-700'
                          : 'bg-white border-gray-300 hover:bg-gray-50'
                      }`}>
                        <option.icon size={16} className={option.color} />
                        <span className="text-sm font-medium">{option.label}</span>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Text Areas */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Strengths
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What are the candidate's key strengths?"
                    value={feedbackData.strengths}
                    onChange={(e) => handleInputChange('strengths', e.target.value)}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Areas for Improvement
                  </label>
                  <textarea
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="What areas need improvement?"
                    value={feedbackData.weaknesses}
                    onChange={(e) => handleInputChange('weaknesses', e.target.value)}
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional Comments
                </label>
                <textarea
                  rows={4}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Any additional feedback or observations..."
                  value={feedbackData.comments}
                  onChange={(e) => handleInputChange('comments', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Next Round Recommendation
                </label>
                <textarea
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  placeholder="Recommendations for the next round or final decision..."
                  value={feedbackData.nextRoundRecommendation}
                  onChange={(e) => handleInputChange('nextRoundRecommendation', e.target.value)}
                />
              </div>

              {/* Submit Button */}
              <div className="flex justify-end pt-6 border-t">
                <button
                  onClick={handleSubmitFeedback}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium flex items-center gap-2 transition-colors"
                >
                  <Send size={18} />
                  Submit Feedback
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 p-6" style={fontStyle}>
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="bg-white rounded-xl shadow-sm border p-6 mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">Interview Feedback</h1>
              <p className="text-gray-600">Submit feedback for completed interviews</p>
            </div>
            <div className="bg-blue-50 px-4 py-2 rounded-lg">
              <span className="text-blue-700 font-medium">{pendingInterviews.length} Pending</span>
            </div>
          </div>
        </div>

        {/* Pending Interviews */}
        <div className="bg-white rounded-xl shadow-sm border">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold flex items-center gap-2">
              <Clock size={20} className="text-orange-600" />
              Pending Feedback
            </h2>
          </div>
          
          <div className="divide-y">
            {pendingInterviews.map((interview) => (
              <div key={interview.id} className="p-6 hover:bg-gray-50 transition-colors">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                      <User size={20} className="text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{interview.candidateName}</h3>
                      <p className="text-gray-600">{interview.position}</p>
                      <div className="flex items-center gap-4 mt-1 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar size={14} />
                          {interview.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock size={14} />
                          {interview.time}
                        </span>
                        <span>Interviewer: {interview.interviewer}</span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-4">
                    <div className={`px-3 py-1 rounded-full border text-sm font-medium flex items-center gap-2 ${getRoundColor(interview.round)}`}>
                      {getRoundIcon(interview.round)}
                      {interview.round}
                    </div>
                    <button
                      onClick={() => setSelectedCandidate(interview)}
                      className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg font-medium transition-colors"
                    >
                      Submit Feedback
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default InterviewFeedback;